import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Warehouse, AlertTriangle, Check, Send, FileText, Download, ShieldCheck, Clock, Package, MessageSquare } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { getStoreQueue, storeAllocate, getReadyToShipQueue, pushToLogistics, pushLogisticsByInvoice, getFirms, getStoreVerifications, confirmVerification, releasePendingAllocation, setStoreRemarks, getSalesOrder } from '../../services/taskApi';
import { fileUrl } from '../../utils/fileUrl';

const FlowStore = () => {
  const { isDark } = useOutletContext();
  const [tab, setTab] = useState('tally'); // 'tally' | 'ship' | 'verify'
  const [items, setItems] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState('');
  const [allocQty, setAllocQty] = useState({});
  const [allocateOnly, setAllocateOnly] = useState({}); // item_id -> allocate qty (split mode)
  const [shortClose, setShortClose] = useState({});    // item_id -> bool
  const [confirmQty, setConfirmQty] = useState({});
  const [processing, setProcessing] = useState(null);
  const [allocConfirm, setAllocConfirm] = useState(null); // { itemId, ordered, available, allocate, shortage }
  const [shortCloseDialog, setShortCloseDialog] = useState(null); // { itemId, opts, reason }
  const [releaseDialog, setReleaseDialog] = useState(null); // { item } for Partially Allocated rows
  const [releaseForm, setReleaseForm] = useState({ action: 'allocate', quantity: '' });
  const [remarkEditFor, setRemarkEditFor] = useState(null); // item_id whose remarks dialog is open
  const [remarkDraft, setRemarkDraft] = useState('');
  const [search, setSearch] = useState('');
  const [groupSO, setGroupSO] = useState(false);
  const [soPreview, setSoPreview] = useState(null);
  const openSoPreview = async (orderId) => {
    setSoPreview({ loading: true, order: null });
    try { const res = await getSalesOrder(orderId); setSoPreview({ loading: false, order: res.data }); }
    catch { toast.error('Failed to load SO'); setSoPreview(null); }
  };

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const fetcher = tab === 'tally' ? getStoreQueue : tab === 'ship' ? getReadyToShipQueue : getStoreVerifications;
      const [sRes, fRes] = await Promise.all([fetcher(filterCompany || undefined, search || undefined), getFirms()]);
      setItems(sRes.data || []);
      setFirms(fRes.data || []);
    } catch (err) { console.error('FlowStore: fetch failed', err); }
    finally { setLoading(false); }
  }, [filterCompany, tab, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAllocate = async (itemId) => {
    const qty = parseInt(allocQty[itemId] ?? '');
    if (isNaN(qty) || qty < 0) { toast.error('Enter valid available quantity (0 or more)'); return; }
    const item = items.find(i => i.id === itemId);
    const ordered = item?.quantity_approved || item?.quantity_ordered || 0;
    const allocateOverride = (allocateOnly[itemId] !== undefined && allocateOnly[itemId] !== '')
      ? parseInt(allocateOnly[itemId]) : null;
    const willAllocate = allocateOverride !== null ? allocateOverride : Math.min(qty, ordered);

    // If user wants to allocate LESS than ordered while stock has enough, show choice modal
    if (allocateOverride !== null && willAllocate < ordered && qty >= ordered && !shortClose[itemId]) {
      setAllocConfirm({ itemId, ordered, available: qty, allocate: willAllocate, shortage: ordered - willAllocate });
      return;
    }
    await doAllocate(itemId, { available_qty: qty, allocate_qty: allocateOverride, short_close: shortClose[itemId] });
  };

  const doAllocate = async (itemId, opts) => {
    // Short-close needs a reason. Use a proper in-app dialog instead of window.prompt —
    // the preview/production app runs inside a sandboxed iframe where window.prompt is
    // silently blocked, so the reason box never appeared. The dialog works everywhere.
    if (opts.short_close && !opts.reason) {
      setAllocConfirm(null);
      setShortCloseDialog({ itemId, opts, reason: '' });
      return;
    }
    const payload = { available_qty: opts.available_qty };
    if (opts.allocate_qty !== null && opts.allocate_qty !== undefined) payload.allocate_qty = opts.allocate_qty;
    if (opts.short_close) {
      payload.short_close_remainder = true;
      payload.reason = opts.reason;
    }
    if (opts.keep_pending) payload.keep_pending_allocation = true;
    setProcessing(itemId);
    try {
      const res = await storeAllocate(itemId, payload);
      toast.success(res.data.message);
      setAllocConfirm(null);
      setShortCloseDialog(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const submitShortClose = () => {
    if (!shortCloseDialog) return;
    const reason = (shortCloseDialog.reason || '').trim();
    if (reason.length < 5) { toast.error('A valid reason is required (min 5 characters)'); return; }
    doAllocate(shortCloseDialog.itemId, { ...shortCloseDialog.opts, reason });
  };

  const handleReleasePending = async () => {
    if (!releaseDialog) return;
    const item = releaseDialog.item;
    const pending = item.quantity_pending_allocation || 0;
    const qty = parseInt(releaseForm.quantity || pending);
    if (qty < 1 || qty > pending) { toast.error(`Quantity must be 1–${pending}`); return; }
    setProcessing(item.id);
    try {
      const res = await releasePendingAllocation(item.id, { action: releaseForm.action, quantity: qty });
      toast.success(res.data.message);
      setReleaseDialog(null);
      setReleaseForm({ action: 'allocate', quantity: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const handlePush = async (itemId) => {
    setProcessing(itemId);
    try {
      const res = await pushToLogistics(itemId);
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const handlePushInvoice = async (invoiceNo) => {
    setProcessing(`inv:${invoiceNo}`);
    try {
      const res = await pushLogisticsByInvoice({ tally_invoice_no: invoiceNo });
      toast.success(res.data.message || 'Invoice pushed');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const openRemarks = (item) => {
    setRemarkEditFor(item.id);
    setRemarkDraft(item.store_remarks || '');
  };

  const saveRemarks = async () => {
    if (!remarkEditFor) return;
    setProcessing(remarkEditFor);
    try {
      const res = await setStoreRemarks(remarkEditFor, { remarks: remarkDraft });
      toast.success(res.data.message || 'Remarks saved');
      setRemarkEditFor(null);
      setRemarkDraft('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const handleConfirmVerification = async (ver) => {
    const cq = parseInt(confirmQty[ver.id] ?? ver.received_qty);
    if (!cq || cq < 1 || cq > ver.received_qty) { toast.error(`Confirm qty must be 1–${ver.received_qty}`); return; }
    setProcessing(ver.id);
    try {
      const res = await confirmVerification(ver.id, { confirmed_qty: cq });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(null); }
  };

  const getFirmName = (id) => firms.find(f => f.id === id)?.name || id;

  // Bill-wise grouping for the "ship" (Pack & Push) tab — Store should push the entire invoice in one click.
  const invoiceGroups = useMemo(() => {
    if (tab !== 'ship') return [];
    const groups = {};
    for (const it of items) {
      const k = (it.tally_invoice_no || '').trim() || '__no_invoice__';
      if (!groups[k]) groups[k] = { invoice: k === '__no_invoice__' ? '' : k, items: [] };
      groups[k].items.push(it);
    }
    return Object.values(groups);
  }, [items, tab]);

  return (
    <div className="space-y-4" data-testid="flow-store">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Store Dashboard</h1>
          <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Tally inventory check & dispatch packing</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input placeholder="Search SO #, item, our item name, customer..." value={search} onChange={e => setSearch(e.target.value)}
            className={`h-9 w-64 text-xs ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : ''}`} data-testid="store-search-input" />
          <Button size="sm" variant={groupSO ? 'default' : 'outline'} onClick={() => setGroupSO(g => !g)}
            className={groupSO ? 'bg-blue-600 hover:bg-blue-700 h-9 text-xs' : `h-9 text-xs ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}
            data-testid="store-group-so-toggle">SO Grouping {groupSO ? 'ON' : 'OFF'}</Button>
          <select className={`h-9 rounded-lg text-xs px-3 border ${selectCls}`} value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
            <option value="">All Companies</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>

      <Dialog open={!!soPreview} onOpenChange={(v) => { if (!v) setSoPreview(null); }}>
        <DialogContent className={`max-w-2xl max-h-[85vh] overflow-y-auto ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="store-so-preview-dialog">
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>
              {soPreview?.order ? `${soPreview.order.so_number} — ${soPreview.order.customer_name}` : 'Sales Order'}
            </DialogTitle>
          </DialogHeader>
          {soPreview?.loading ? (
            <div className="text-center py-8 text-sm opacity-50">Loading…</div>
          ) : soPreview?.order && (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-3 gap-2">
                <div><span className="opacity-50">Agent</span><p className="font-medium">{soPreview.order.agent_name || '—'}</p></div>
                <div><span className="opacity-50">SO Date</span><p className="font-medium">{(soPreview.order.created_at || '').slice(0, 10)}</p></div>
                <div><span className="opacity-50">Customer PO</span><p className="font-medium">{soPreview.order.customer_po_number || '—'}</p></div>
              </div>
              <table className="w-full text-xs"><tbody>
                {(soPreview.order.items || []).map(it => (
                  <tr key={it.id} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                    <td className="py-1.5 pr-2">{it.item_name}</td>
                    <td className="py-1.5 pr-2">{it.quantity_ordered}</td>
                    <td className="py-1.5">{it.fulfillment_status}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
        {[
          { k: 'tally', label: 'Tally Stock Check' },
          { k: 'verify', label: 'Verify (Purchase Received)' },
          { k: 'ship', label: 'Pack & Push to Logistics' }
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`store-tab-${t.k}`}
            className={`flex-1 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
              tab === t.k
                ? isDark ? 'bg-white/10 text-white shadow' : 'bg-white text-gray-900 shadow-sm'
                : isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'
            }`}
          >{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className={`h-16 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div>
      ) : items.length === 0 ? (
        <Card className={cardCls}><CardContent className="p-8 text-center">
          <Warehouse size={40} className={`mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-white/40' : 'text-gray-500'}>
            {tab === 'tally' ? 'No items awaiting Tally check' : tab === 'verify' ? 'No items awaiting verification from Purchase' : 'No billed items awaiting dispatch'}
          </p>
        </CardContent></Card>
      ) : tab === 'verify' ? (
        <Card className={cardCls}>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SO #</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Item</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Received Qty</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Notes</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Confirm Qty</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(ver => (
                    <tr key={ver.id} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-50 hover:bg-gray-50'}`}>
                      <td className={`py-3 px-2 font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{ver.so_number}</td>
                      <td className={`py-3 px-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{ver.item_name}</td>
                      <td className={`py-3 px-2 text-center font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{ver.received_qty}</td>
                      <td className={`py-3 px-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{ver.notes || '-'}</td>
                      <td className="py-3 px-2 text-center">
                        <Input type="number" min="1" max={ver.received_qty}
                          className={`w-20 h-7 text-xs text-center mx-auto ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
                          defaultValue={ver.received_qty}
                          onChange={e => setConfirmQty(q => ({ ...q, [ver.id]: e.target.value }))}
                          data-testid={`verify-qty-${ver.id}`}
                        />
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 px-3 text-xs"
                          disabled={processing === ver.id}
                          onClick={() => handleConfirmVerification(ver)}
                          data-testid={`confirm-verify-${ver.id}`}>
                          {processing === ver.id ? '...' : <><ShieldCheck size={12} className="mr-1" /> Verify</>}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : tab === 'ship' ? (
        <div className="space-y-3" data-testid="ship-invoice-groups">
          {invoiceGroups.map((grp) => {
            const firstItem = grp.items[0];
            const totalUnits = grp.items.reduce((s, it) => s + (it.quantity_billed || it.quantity_allocated_to_bill || 0), 0);
            const procKey = `inv:${grp.invoice}`;
            return (
              <Card key={grp.invoice || `noinv-${firstItem?.id}`} className={cardCls}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Tally Invoice</span>
                        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{grp.invoice || '— (no invoice)'}</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/15 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                          {grp.items.length} item{grp.items.length > 1 ? 's' : ''} · {totalUnits} units
                        </span>
                      </div>
                      <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        Customer: <b>{firstItem?.order_info?.customer_name || '-'}</b>
                        <span className="mx-2">·</span>
                        SO: <b>{Array.from(new Set(grp.items.map(it => it.so_number))).join(', ')}</b>
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 h-9 px-4 text-xs w-full sm:w-auto"
                      disabled={!grp.invoice || processing === procKey}
                      onClick={() => handlePushInvoice(grp.invoice)}
                      data-testid={`push-invoice-${grp.invoice || 'noinv'}`}
                      title={!grp.invoice ? 'Bill-wise push needs a Tally invoice number' : 'Push the entire invoice to logistics in one click'}
                    >
                      {processing === procKey ? '...' : <><Send size={14} className="mr-1.5" /> Push Entire Invoice</>}
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[560px]">
                      <thead>
                        <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                          <th className={`text-left py-2 px-2 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SO #</th>
                          <th className={`text-left py-2 px-2 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Company</th>
                          <th className={`text-left py-2 px-2 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Item / SKU</th>
                          <th className={`text-center py-2 px-2 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Billed Qty</th>
                          <th className={`text-center py-2 px-2 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Files</th>
                          <th className={`text-right py-2 px-2 font-medium uppercase tracking-wider text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Single-item</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grp.items.map((item) => (
                          <tr key={item.id} className={`border-b last:border-b-0 ${isDark ? 'border-white/5' : 'border-gray-50'}`}>
                            <td className={`py-2 px-2 font-medium`}>
                              <button onClick={() => openSoPreview(item.order_id)} className={`underline-offset-2 hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`} data-testid={`store-so-preview-${item.id}`}>{item.so_number}</button>
                            </td>
                            <td className={`py-2 px-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{getFirmName(item.order_info?.company_id)}</td>
                            <td className={`py-2 px-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              <div>{item.item_name}</div>
                              {item.purchase_item_name && <div className="text-[9px] text-purple-400" title="Purchase team's item name">Purch: {item.purchase_item_name}</div>}
                              {item.description && <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{item.description}</div>}
                            </td>
                            <td className={`py-2 px-2 text-center font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.quantity_billed || item.quantity_allocated_to_bill}</td>
                            <td className="py-2 px-2 text-center">
                              <div className="flex items-center justify-center gap-2">
                                {item.order_info?.po_proof_url && (
                                  <a href={fileUrl(item.order_info.po_proof_url)} target="_blank" rel="noreferrer" className={`text-[10px] ${isDark ? 'text-blue-400' : 'text-blue-600'}`} title="Customer PO"><FileText size={11} /></a>
                                )}
                                {item.drawing_url && (
                                  <a href={fileUrl(item.drawing_url)} target="_blank" rel="noreferrer" className={`text-[10px] ${isDark ? 'text-green-400' : 'text-green-600'}`} title="Drawing"><Download size={11} /></a>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-2 text-right">
                              <Button
                                size="sm" variant="ghost"
                                className={`h-7 px-2 text-[10px] ${isDark ? 'text-white/50 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-100'}`}
                                disabled={processing === item.id}
                                onClick={() => handlePush(item.id)}
                                data-testid={`push-logistics-${item.id}`}
                                title="Push only this item (admin / recovery use only)"
                              >
                                Push only this
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className={cardCls}>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SO #</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Company</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Item / SKU</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Qty</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Files</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Tally Available</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(groupSO ? [...items].sort((a, b) => (a.so_number || '').localeCompare(b.so_number || '')) : items).map((item) => (
                    <tr key={item.id} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-50 hover:bg-gray-50'} transition-colors`}>
                      <td className={`py-3 px-2 font-medium`}>
                        <button onClick={() => openSoPreview(item.order_id)} className={`underline-offset-2 hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`} data-testid={`store-so-preview2-${item.id}`}>{item.so_number}</button>
                      </td>
                      <td className={`py-3 px-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{getFirmName(item.order_info?.company_id)}</td>
                      <td className={`py-3 px-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <div>{item.item_name}</div>
                        {item.purchase_item_name && <div className="text-[9px] text-purple-400" title="Purchase team's item name">Purch: {item.purchase_item_name}</div>}
                        {item.description && <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{item.description}</div>}
                        {item.admin_remarks && (
                          <div className={`mt-1 text-[10px] px-2 py-1 rounded border-l-2 border-amber-500 ${isDark ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-800'}`} title={`By ${item.admin_remarks_by || '-'}`}>
                            💬 <span className="font-medium">Admin:</span> {item.admin_remarks}
                          </div>
                        )}
                        {item.store_remarks && (
                          <div className={`mt-1 text-[10px] px-2 py-1 rounded border-l-2 border-sky-500 ${isDark ? 'bg-sky-500/10 text-sky-200' : 'bg-sky-50 text-sky-800'}`} title={`By ${item.store_remarks_by || '-'}`}>
                            📦 <span className="font-medium">Store note (for Purchase):</span> {item.store_remarks}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => openRemarks(item)}
                          className={`mt-1 inline-flex items-center gap-1 text-[10px] ${isDark ? 'text-white/40 hover:text-sky-300' : 'text-gray-500 hover:text-sky-700'}`}
                          data-testid={`open-store-remark-${item.id}`}
                          title="Add a remark visible to Purchase (e.g., grade hint, supplier preference)"
                        >
                          <MessageSquare size={10} /> {item.store_remarks ? 'Edit note for Purchase' : 'Add note for Purchase'}
                        </button>
                      </td>
                      <td className={`py-3 px-2 text-center font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.quantity_approved || item.quantity_ordered}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {item.order_info?.po_proof_url && (
                            <a href={fileUrl(item.order_info.po_proof_url)} target="_blank" rel="noreferrer"
                               className={`flex items-center gap-1 text-[10px] ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                               title="Download PO">
                              <FileText size={12} /> PO
                            </a>
                          )}
                          {item.drawing_url && (
                            <a href={fileUrl(item.drawing_url)} target="_blank" rel="noreferrer"
                               className={`flex items-center gap-1 text-[10px] ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'}`}
                               title="Download Drawing">
                              <Download size={12} /> DWG
                            </a>
                          )}
                          {!item.order_info?.po_proof_url && !item.drawing_url && <span className={isDark ? 'text-white/20' : 'text-gray-300'}>-</span>}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {(item.quantity_pending_allocation || 0) > 0 ? (
                          <div className={`text-[10px] font-semibold px-2 py-1 rounded-md inline-flex items-center gap-1 ${isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-50 text-amber-700'}`}>
                            <Clock size={10} /> PENDING: {item.quantity_pending_allocation} units
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1 items-center">
                            <Input
                              type="number" min="0"
                              className={`w-20 h-7 text-xs text-center ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
                              placeholder="In Tally"
                              value={allocQty[item.id] ?? ''}
                              onChange={e => setAllocQty(q => ({ ...q, [item.id]: e.target.value }))}
                              data-testid={`tally-qty-${item.id}`}
                            />
                            <Input
                              type="number" min="0"
                              max={Math.min(parseInt(allocQty[item.id] || 0), item.quantity_approved || item.quantity_ordered)}
                              className={`w-20 h-6 text-[10px] text-center ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
                              placeholder="Allocate"
                              value={allocateOnly[item.id] ?? ''}
                              onChange={e => setAllocateOnly(q => ({ ...q, [item.id]: e.target.value }))}
                              data-testid={`allocate-only-${item.id}`}
                              title="Optional: allocate less than available (rest goes to indent or short-close)"
                            />
                            <label className={`flex items-center gap-1 text-[9px] cursor-pointer ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                              <input type="checkbox" className="h-2.5 w-2.5"
                                checked={!!shortClose[item.id]}
                                onChange={e => setShortClose(q => ({ ...q, [item.id]: e.target.checked }))}
                                data-testid={`short-close-${item.id}`}
                              />
                              short-close
                            </label>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        {(item.quantity_pending_allocation || 0) > 0 ? (
                          <Button
                            size="sm" className="bg-amber-600 hover:bg-amber-700 h-7 px-3 text-xs"
                            disabled={processing === item.id}
                            onClick={() => { setReleaseDialog({ item }); setReleaseForm({ action: 'allocate', quantity: String(item.quantity_pending_allocation || 0) }); }}
                            data-testid={`release-pending-${item.id}`}
                          >
                            Release Pending
                          </Button>
                        ) : (
                              <Button
                                size="sm" className="bg-green-600 hover:bg-green-700 h-7 px-3 text-xs"
                                disabled={processing === item.id || !(allocQty[item.id] !== undefined && allocQty[item.id] !== '')}
                                onClick={() => handleAllocate(item.id)}
                                data-testid={`allocate-${item.id}`}
                              >
                                {processing === item.id ? '...' : <><Check size={12} className="mr-1" /> Allocate</>}
                              </Button>
                            )}
                          </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className={`flex items-start gap-2 p-3 rounded-lg ${isDark ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
        <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <p className={`text-xs ${isDark ? 'text-amber-400/80' : 'text-amber-700'}`}>
          {tab === 'tally'
            ? 'Enter the available quantity from Tally. Full stock → ready for billing. Partial/zero → purchase indent auto-created for the shortage. Or hold the remainder as PENDING for later allocation.'
            : tab === 'ship'
              ? 'Items are grouped by their Tally invoice. Click "Push Entire Invoice" to release the whole bill to Logistics in one go.'
              : 'These items came back from Purchase. Verify the received quantity matches what was physically delivered before it goes for packing.'}
        </p>
      </div>

      {/* Store remarks dialog — note for Purchase team */}
      <Dialog open={!!remarkEditFor} onOpenChange={(v) => { if (!v) { setRemarkEditFor(null); setRemarkDraft(''); } }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="store-remark-dialog">
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Note for Purchase team</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              Whatever you write here is visible to the Purchase user on the indent — use it for grade hints, preferred supplier, packing notes etc.
            </p>
            <textarea
              className={`w-full min-h-[110px] rounded-lg p-3 text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder:text-white/30' : 'bg-white border border-gray-300 text-gray-900'}`}
              placeholder="e.g. Use only A-grade supplier; previously bought at ₹450 from XYZ Traders."
              value={remarkDraft}
              onChange={(e) => setRemarkDraft(e.target.value)}
              data-testid="store-remark-input"
              maxLength={500}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''} onClick={() => { setRemarkEditFor(null); setRemarkDraft(''); }}>Cancel</Button>
              <Button type="button" className="bg-sky-600 hover:bg-sky-700" onClick={saveRemarks} disabled={processing === remarkEditFor} data-testid="store-remark-save">
                {processing === remarkEditFor ? 'Saving…' : 'Save note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Short-close reason dialog — replaces window.prompt (blocked inside sandboxed iframe) */}
      <Dialog open={!!shortCloseDialog} onOpenChange={(v) => { if (!v) setShortCloseDialog(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="store-short-close-dialog">
          {shortCloseDialog && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Short-close remaining quantity</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2 text-sm">
                <div className={`p-3 rounded-lg text-xs ${isDark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
                  The unallocated remainder of this item will be permanently short-closed (no purchase indent will be raised). A reason is required and stored on the item for audit.
                </div>
                <div>
                  <label className={`text-xs font-medium block mb-1 ${isDark ? 'text-white/60' : 'text-gray-700'}`}>Reason *</label>
                  <textarea
                    className={`w-full min-h-[90px] rounded-lg p-3 text-xs ${isDark ? 'bg-white/5 border border-white/10 text-white placeholder:text-white/30' : 'bg-white border border-gray-300 text-gray-900'}`}
                    placeholder="e.g. Customer cancelled remaining qty; item discontinued by supplier."
                    value={shortCloseDialog.reason}
                    onChange={(e) => setShortCloseDialog(d => ({ ...d, reason: e.target.value }))}
                    data-testid="store-short-close-reason"
                    maxLength={500}
                    autoFocus
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''} onClick={() => setShortCloseDialog(null)}>Cancel</Button>
                  <Button type="button" className="bg-red-600 hover:bg-red-700" onClick={submitShortClose} disabled={processing === shortCloseDialog.itemId} data-testid="store-short-close-submit">
                    {processing === shortCloseDialog.itemId ? 'Short-closing…' : 'Confirm short-close'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Choice modal: enough stock but user wants partial → ask whether to indent or keep pending */}
      <Dialog open={!!allocConfirm} onOpenChange={(v) => { if (!v) setAllocConfirm(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`}>
          {allocConfirm && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Stock is sufficient — what next?</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2 text-sm">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700'} text-xs`}>
                  Tally has <b>{allocConfirm.available}</b> pcs (enough for the full order of <b>{allocConfirm.ordered}</b>),
                  but you're allocating only <b>{allocConfirm.allocate}</b>. What do you want to do with the remaining <b>{allocConfirm.shortage}</b> units?
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <Button className="bg-amber-600 hover:bg-amber-700 h-10 justify-start text-left" data-testid="alloc-keep-pending"
                    onClick={() => doAllocate(allocConfirm.itemId, { available_qty: allocConfirm.available, allocate_qty: allocConfirm.allocate, keep_pending: true })}>
                    <Clock size={14} className="mr-2" /> Keep {allocConfirm.shortage} as PENDING in store (no indent — I'll revisit)
                  </Button>
                  <Button className="bg-blue-600 hover:bg-blue-700 h-10 justify-start text-left" data-testid="alloc-create-indent"
                    onClick={() => doAllocate(allocConfirm.itemId, { available_qty: allocConfirm.available, allocate_qty: allocConfirm.allocate })}>
                    <FileText size={14} className="mr-2" /> Create purchase indent anyway for {allocConfirm.shortage} units
                  </Button>
                  <Button variant="outline" className={`h-10 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`} onClick={() => setAllocConfirm(null)}>Cancel</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Release Pending dialog */}
      <Dialog open={!!releaseDialog} onOpenChange={(v) => { if (!v) { setReleaseDialog(null); setReleaseForm({ action: 'allocate', quantity: '' }); } }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`}>
          {releaseDialog && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Release Pending — {releaseDialog.item.item_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2 text-sm">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5 text-white/70' : 'bg-gray-50 text-gray-700'} text-xs`}>
                  Pending in store: <b className="text-amber-400">{releaseDialog.item.quantity_pending_allocation || 0}</b> units<br/>
                  Already allocated: <b className="text-green-400">{releaseDialog.item.quantity_allocated_to_bill || 0}</b> · Ordered: <b>{releaseDialog.item.quantity_approved || releaseDialog.item.quantity_ordered}</b>
                </div>
                <div>
                  <label className={`text-xs font-medium block mb-1 ${isDark ? 'text-white/60' : 'text-gray-700'}`}>What do you want to do?</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setReleaseForm(f => ({ ...f, action: 'allocate' }))}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border ${releaseForm.action === 'allocate'
                        ? 'bg-green-600 text-white border-green-600'
                        : isDark ? 'bg-white/5 border-white/10 text-white/60' : 'bg-white border-gray-200 text-gray-600'
                      }`} data-testid="release-action-allocate">
                      Allocate from Stock
                    </button>
                    <button type="button" onClick={() => setReleaseForm(f => ({ ...f, action: 'indent' }))}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border ${releaseForm.action === 'indent'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : isDark ? 'bg-white/5 border-white/10 text-white/60' : 'bg-white border-gray-200 text-gray-600'
                      }`} data-testid="release-action-indent">
                      Generate Indent
                    </button>
                  </div>
                </div>
                <div>
                  <label className={`text-xs font-medium block mb-1 ${isDark ? 'text-white/60' : 'text-gray-700'}`}>Quantity</label>
                  <Input type="number" min={1} max={releaseDialog.item.quantity_pending_allocation || 0}
                    className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
                    value={releaseForm.quantity}
                    onChange={e => setReleaseForm(f => ({ ...f, quantity: e.target.value }))}
                    data-testid="release-qty" />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className={`flex-1 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`} onClick={() => setReleaseDialog(null)}>Cancel</Button>
                  <Button onClick={handleReleasePending} disabled={processing === releaseDialog.item.id} className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50" data-testid="release-submit">
                    {processing === releaseDialog.item.id ? '...' : 'Confirm'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlowStore;
